import type { LocationConfig } from './types';
import { beep } from './audio';

export interface TimerCallbacks {
  onTick: (s: TimerState) => void;
  onIntervalEnd: () => void;
}

export interface TimerState {
  running: boolean;
  intervalIdx: number;
  remainingMs: number;
  completedCycles: number;
}

export class Timer {
  private cfg: LocationConfig;
  private cb: TimerCallbacks;
  private state: TimerState;
  private lastTick: number | null = null;

  constructor(cfg: LocationConfig, cb: TimerCallbacks) {
    this.cfg = cfg;
    this.cb = cb;
    this.state = {
      running: false,
      intervalIdx: 0,
      remainingMs: cfg.schedule.intervals[0].durationSec * 1000,
      completedCycles: 0
    };
  }

  updateConfig(cfg: LocationConfig): void {
    this.cfg = cfg;
    this.reset();
  }

  getState(): TimerState { return this.state; }
  currentInterval() { return this.cfg.schedule.intervals[this.state.intervalIdx]; }

  start(): void {
    if (this.state.running) return;
    this.state.running = true;
    this.lastTick = null;
    requestAnimationFrame(this.loop);
  }

  pause(): void { this.state.running = false; }

  reset(): void {
    this.state.running = false;
    this.state.intervalIdx = 0;
    this.state.completedCycles = 0;
    this.state.remainingMs = this.cfg.schedule.intervals[0].durationSec * 1000;
    this.cb.onTick(this.state);
  }

  skip(): void { this.state.remainingMs = 0; }

  private loop = (now: number): void => {
    if (!this.state.running) { this.lastTick = null; return; }
    if (this.lastTick == null) this.lastTick = now;
    const delta = now - this.lastTick;
    this.lastTick = now;

    const prevSec = Math.ceil(this.state.remainingMs / 1000);
    this.state.remainingMs -= delta;
    const newSec = Math.ceil(this.state.remainingMs / 1000);

    if (this.cfg.sound && prevSec !== newSec && newSec <= 3 && newSec >= 1) {
      beep('count');
    }

    if (this.state.remainingMs <= 0) {
      this.advance();
    } else {
      this.cb.onTick(this.state);
    }
    requestAnimationFrame(this.loop);
  };

  private advance(): void {
    if (this.cfg.sound) beep('end');
    this.cb.onIntervalEnd();
    this.state.intervalIdx++;
    if (this.state.intervalIdx >= this.cfg.schedule.intervals.length) {
      this.state.intervalIdx = 0;
      this.state.completedCycles++;
      if (!this.cfg.schedule.loop) {
        this.state.running = false;
        this.state.remainingMs = 0;
        this.cb.onTick(this.state);
        return;
      }
    }
    this.state.remainingMs = this.cfg.schedule.intervals[this.state.intervalIdx].durationSec * 1000;
    this.cb.onTick(this.state);
  }
}
